import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

export default function NewBillingModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [admissions, setAdmissions] = useState([]);
  const [f, setF] = useState({
    admission_id: "",
    medication_charges: "0", procedure_charges: "0",
    consultation_charges: "0", other_charges: "0",
    discount_applied: "0",
    payment_method: "cash",
  });

  useEffect(() => {
    if (open) api.get("/ipd/admissions?status=active").then((r) => setAdmissions(r.data)).catch(() => {});
  }, [open]);

  const num = (v) => Number(v || 0) || 0;
  const subtotal = num(f.medication_charges) + num(f.procedure_charges) +
                   num(f.consultation_charges) + num(f.other_charges);
  const total = useMemo(() => Math.max(0, subtotal - num(f.discount_applied)),
                        [subtotal, f.discount_applied]);

  const submit = async () => {
    if (!f.admission_id) { toast.error("Select an admission"); return; }
    setSaving(true);
    try {
      await api.post("/ipd/billing", {
        admission_id: f.admission_id,
        medication_charges: num(f.medication_charges),
        procedure_charges: num(f.procedure_charges),
        consultation_charges: num(f.consultation_charges),
        other_charges: num(f.other_charges),
        discount_applied: num(f.discount_applied),
        payment_method: f.payment_method,
      });
      toast.success("Bill created");
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="ipd-new-bill-btn">
          <Plus size={14} className="mr-1" /> Create Bill
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="ipd-new-bill-modal">
        <DialogHeader><DialogTitle>Create Bill</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Admission *</Label>
            <Select value={f.admission_id} onValueChange={(v) => setF({ ...f, admission_id: v })}>
              <SelectTrigger data-testid="ipd-bill-admission"><SelectValue placeholder="Choose active admission" /></SelectTrigger>
              <SelectContent>
                {admissions.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.patient_name || `Admission ${a.id.slice(0, 8)}`}
                    {a.ward_name ? ` · ${a.ward_name}` : ""}
                    {a.bed_number ? ` · ${a.bed_number}` : ""}
                    {` · admitted ${a.admission_date}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Bed charges are auto-calculated from ward rate × days.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Medicines (₹)</Label>
              <Input type="number" value={f.medication_charges}
                     onChange={(e) => setF({ ...f, medication_charges: e.target.value })} /></div>
            <div><Label>Procedures (₹)</Label>
              <Input type="number" value={f.procedure_charges}
                     onChange={(e) => setF({ ...f, procedure_charges: e.target.value })} /></div>
            <div><Label>Consultation (₹)</Label>
              <Input type="number" value={f.consultation_charges}
                     onChange={(e) => setF({ ...f, consultation_charges: e.target.value })} /></div>
            <div><Label>Other (₹)</Label>
              <Input type="number" value={f.other_charges}
                     onChange={(e) => setF({ ...f, other_charges: e.target.value })} /></div>
            <div><Label>Discount (₹)</Label>
              <Input type="number" value={f.discount_applied}
                     onChange={(e) => setF({ ...f, discount_applied: e.target.value })} /></div>
            <div><Label>Payment Method</Label>
              <Select value={f.payment_method} onValueChange={(v) => setF({ ...f, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border-t pt-3 flex justify-between text-sm">
            <span className="text-slate-500">Subtotal (excl. bed)</span>
            <span className="font-semibold">₹{subtotal.toLocaleString("en-IN")}</span>
          </div>
          <div className="flex justify-between text-base">
            <span className="font-semibold">Total (bed charges added server-side)</span>
            <span className="font-bold text-emerald-700" data-testid="ipd-bill-total">
              ₹{total.toLocaleString("en-IN")}+
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="ipd-bill-submit">{saving ? "Creating…" : "Create Bill"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
